import { useRecoilValue } from "recoil";
import { stringsState } from "../states/localeState";
import { Timestamp } from "../types/core";
import { useTime } from "./useTime";
import { getRecoil } from "../providers/RecoilAccessProvider";
import { messageApiState } from "../states/appState";
import { isDesktopEnvironment } from "../utils/app";
import { ReactNode, useCallback } from "react";
import parse from "html-react-parser";
import { downloadFileFromUrl } from "../utils/files";

type Formatted = number | string;

export function useStrings() {
  const strings = useRecoilValue(stringsState);
  const time = useTime();
  // the same function while the language stays, so memos and table columns that use it don't rebuild on every render
  const format = useCallback((str: string, ...values: Formatted[]) => {
    for (let i = 0; i < values.length; i++) {
      if (!values[i] && values[i] !== 0) {
        values[i] = "";
      }
    }
    return strings.formatString(str, ...values) as string;
  }, [strings]);
  const formatDate = (str: string, ts: Timestamp) => strings.formatString(str, time.formatDateOnly(ts)) as string;

  const formatHtml = (htmlStr: string, ...values: Formatted[]): ReactNode => {
    const isTauri = isDesktopEnvironment();
    // First substitute values into htmlStr (reuse your format logic or an adapted one)
    const filled = format(htmlStr, ...values);

    // Quick check: only parse / replace if in Tauri and if string likely has anchor with download or external link
    if (isTauri && (filled.includes("<a ") && filled.includes("download"))) {
      return parse(filled, {
        replace: (domNode: any) => {
          if (domNode.type === "tag" && domNode.name === "a") {
            
            const attribs = (domNode as any).attribs;
            const { href, download } = attribs;
            if (download !== undefined) {
              // Custom anchor for Tauri download case
              return (
                <a
                  href={href}
                  download={download}
                  onClick={(e) => {
                    e.preventDefault();
                    // a parser callback, not a component: read the message api without a hook
                    getRecoil(messageApiState)?.open({ type: "loading", content: strings.General.DownloadStarted });
                    downloadFileFromUrl(href);
                  }}
                  rel="noopener noreferrer"
                >
                  {domNode.children?.map((child: any, idx: number) => child.data || "").join("")}
                </a>
              );
            }
          }
        }
      });
    }

    // Fallback: render raw HTML (or via parse but without replacements) or even dangerouslySetInnerHTML
    return <span dangerouslySetInnerHTML={{ __html: filled }} />;
  };

  return { strings, format, formatDate, formatHtml };
}